import { useState, useEffect } from "react";
import { X, AlertCircle, Search } from "lucide-react";
import creditNoteService from "../services/creditNoteService";
import saleService from "../services/saleService";
import Swal from "sweetalert2";

export default function CreateCreditNoteModal({ onClose, onSuccess }) {
  const [sales, setSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [revertirInventario, setRevertirInventario] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingSales, setLoadingSales] = useState(true);
  const [searchSale, setSearchSale] = useState("");

  // ✅ Cargar ventas elegibles para nota de crédito
  useEffect(() => {
    const fetchSales = async () => {
      try {
        setLoadingSales(true);
        const res = await saleService.getEligibleForCreditNote(searchSale);
        const data = res.data || res;
        
        // ✅ SIMPLIFICADO: El backend ya devuelve solo las ventas elegibles
        // No necesitamos filtrar aquí
        setSales(Array.isArray(data) ? data : []);
        
        console.log("✅ Ventas elegibles recibidas:", data);
        
      } catch (error) {
        console.error("Error cargando ventas:", error);
        Swal.fire("Error", "No se pudieron cargar las ventas", "error");
      } finally {
        setLoadingSales(false);
      }
    };

    // Debounce para la búsqueda
    const timeout = setTimeout(() => {
      fetchSales();
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchSale]);

  // ✅ Seleccionar venta
  const handleSelectSale = (sale) => {
    setSelectedSale(sale);
  };

  // ✅ Validar formulario
  const validateForm = () => {
    if (!selectedSale) {
      Swal.fire("Error", "Debe seleccionar una venta", "warning");
      return false;
    }

    if (!motivo.trim()) {
      Swal.fire("Error", "Debe proporcionar un motivo", "warning");
      return false;
    }

    if (motivo.trim().length < 10) {
      Swal.fire("Error", "El motivo debe tener al menos 10 caracteres", "warning");
      return false;
    }

    if (motivo.trim().length > 500) {
      Swal.fire("Error", "El motivo no puede exceder 500 caracteres", "warning");
      return false;
    }

    return true;
  };

  // ✅ Crear nota de crédito
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Confirmación
    const confirm = await Swal.fire({
      title: "¿Crear Nota de Crédito?",
      html: `
        <div style="text-align: left;">
          <p><strong>Cliente:</strong> ${selectedSale.client?.nombre || "N/A"}</p>
          <p style="margin-bottom: 4px;"><strong>Código Factura:</strong></p>
          <p style="font-size: 11px; font-family: monospace; word-break: break-all; color: #667eea; margin-top: 0; padding: 0 10px;">${selectedSale.invoice?.codigo_generacion || "N/A"}</p>
          <p style="margin-top: 12px;"><strong>Monto:</strong> $${Number(selectedSale.monto_final).toFixed(2)}</p>
          <p><strong>Motivo:</strong> ${motivo}</p>
          <p><strong>Revertir inventario:</strong> ${revertirInventario ? "Sí" : "No"}</p>
        </div>
        <div style="margin-top: 16px; padding: 12px; background: #fef3c7; border-radius: 8px; border-left: 4px solid #f59e0b;">
          <p style="margin: 0; font-size: 13px; color: #92400e;">
            <strong>⚠️ Importante:</strong> Esta acción generará un nuevo DTE tipo 05 
            en el Ministerio de Hacienda y anulará la venta.
          </p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#667eea",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, crear",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    setIsSubmitting(true);

    try {
      const payload = {
        sale_id: selectedSale.id,
        motivo: motivo.trim(),
        revertir_inventario: revertirInventario,
      };

      const response = await creditNoteService.create(payload);

      Swal.fire({
        icon: "success",
        title: "¡Nota de Crédito Creada!",
        html: `
          <p>La nota de crédito se ha emitido correctamente.</p>
          <p style="margin-top: 12px; margin-bottom: 4px;"><strong>Código de Generación:</strong></p>
          <p style="font-size: 11px; color: #667eea; font-weight: 600; font-family: monospace; word-break: break-all; padding: 0 20px; margin-top: 0;">${response.credit_note?.codigo_generacion || response.codigo_generacion}</p>
        `,
        confirmButtonText: "Aceptar",
      });

      onSuccess();
    } catch (error) {
      console.error("Error creando nota de crédito:", error);
      Swal.fire(
        "Error",
        error.response?.data?.error || error.message || "No se pudo crear la nota de crédito",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content card" style={{ maxHeight: "90vh" }}>
            {/* HEADER */}
            <div className="card-header">
              <h3 className="card-title" style={{ margin: 0 }}>
                📄 Nueva Nota de Crédito (DTE 05)
              </h3>
              <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={isSubmitting}>
                <X size={18} />
              </button>
            </div>

            {/* BODY */}
            <div className="card-body" style={{ overflowY: "auto" }}>
              <form onSubmit={handleSubmit}>
                {/* ALERTA INFORMATIVA */}
                <div
                  className="alert alert-info"
                  style={{
                    marginBottom: "20px",
                    display: "flex",
                    gap: "12px",
                    alignItems: "start",
                  }}
                >
                  <AlertCircle size={20} style={{ marginTop: "2px", flexShrink: 0 }} />
                  <div style={{ fontSize: "14px" }}>
                    <strong>¿Qué es una Nota de Crédito?</strong>
                    <p style={{ margin: "8px 0 0 0" }}>
                      Es un DTE tipo 05 que se genera para anular una factura validada por Hacienda.
                      A diferencia de la invalidación, puede crearse en cualquier momento después de
                      la emisión de la factura original.
                    </p>
                  </div>
                </div>

                {/* SELECCIONAR VENTA */}
                <div className="form-group">
                  <label className="form-label">
                    <strong>1. Seleccionar Venta/Factura</strong>
                  </label>

                  {/* Buscador */}
                  <div style={{ position: "relative", marginBottom: "12px" }}>
                    <Search
                      size={18}
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#a0aec0",
                      }}
                    />
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por cliente..."
                      value={searchSale}
                      onChange={(e) => setSearchSale(e.target.value)}
                      style={{ paddingLeft: "40px" }}
                    />
                  </div>

                  {/* Lista de ventas */}
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      background: "#f7fafc",
                    }}
                  >
                    {loadingSales ? (
                      <div className="text-center" style={{ padding: "40px" }}>
                        <div className="spinner" style={{ margin: "0 auto" }}></div>
                        <p style={{ marginTop: "16px", color: "#718096", fontSize: "14px" }}>
                          Cargando ventas...
                        </p>
                      </div>
                    ) : sales.length === 0 ? (
                      <div className="text-center" style={{ padding: "40px" }}>
                        <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
                          {searchSale
                            ? "No se encontraron ventas con ese criterio"
                            : "No hay ventas elegibles para nota de crédito"}
                        </p>
                        <p style={{ color: "#718096", fontSize: "12px", marginTop: "8px" }}>
                          Solo se muestran facturas de Crédito Fiscal (CCF) validadas
                        </p>
                      </div>
                    ) : (
                      sales.map((sale) => (
                        <div
                          key={sale.id}
                          onClick={() => handleSelectSale(sale)}
                          style={{
                            padding: "12px",
                            borderBottom: "1px solid #e2e8f0",
                            cursor: "pointer",
                            background: selectedSale?.id === sale.id ? "#dbeafe" : "white",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (selectedSale?.id !== sale.id) {
                              e.currentTarget.style.background = "#f7fafc";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedSale?.id !== sale.id) {
                              e.currentTarget.style.background = "white";
                            }
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: "#1f2937", marginBottom: "4px" }}>
                                {sale.client?.nombre || "Cliente desconocido"}
                              </div>
                              <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px", fontFamily: "monospace", wordBreak: "break-all", maxWidth: "400px" }}>
                                <strong>Código:</strong> {sale.invoice?.codigo_generacion || "N/A"}
                              </div>
                              <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                                Fecha: {new Date(sale.fecha_venta).toLocaleDateString("es-SV")}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "18px", fontWeight: 700, color: "#667eea" }}>
                                ${Number(sale.monto_final).toFixed(2)}
                              </div>
                              <div
                                style={{
                                  fontSize: "11px",
                                  marginTop: "4px",
                                  padding: "2px 8px",
                                  background: "#d1fae5",
                                  color: "#065f46",
                                  borderRadius: "4px",
                                  display: "inline-block",
                                }}
                              >
                                VALIDADO
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {selectedSale && (
                    <div
                      style={{
                        marginTop: "12px",
                        padding: "12px",
                        background: "#ecfdf5",
                        border: "2px solid #10b981",
                        borderRadius: "8px",
                      }}
                    >
                      <strong style={{ color: "#065f46" }}>✓ Venta seleccionada:</strong>
                      <div style={{ fontSize: "14px", color: "#047857", marginTop: "4px" }}>
                        {selectedSale.client?.nombre} - ${Number(selectedSale.monto_final).toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>

                {/* MOTIVO */}
                <div className="form-group">
                  <label className="form-label">
                    <strong>2. Motivo de la Nota de Crédito</strong>
                  </label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Ej: Devolución de mercadería por defecto de fábrica..."
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    maxLength={500}
                    required
                  />
                  <small style={{ color: "#718096", fontSize: "12px" }}>
                    {motivo.length}/500 caracteres (mínimo 10)
                  </small>
                </div>

                {/* REVERTIR INVENTARIO */}
                <div className="form-group">
                  <label className="form-label">
                    <strong>3. Inventario</strong>
                  </label>
                  <div
                    style={{
                      padding: "12px",
                      background: "#f7fafc",
                      borderRadius: "8px",
                      border: "2px solid #e2e8f0",
                    }}
                  >
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={revertirInventario}
                        onChange={(e) => setRevertirInventario(e.target.checked)}
                        style={{ width: "18px", height: "18px" }}
                      />
                      <span style={{ fontSize: "14px" }}>
                        <strong>Revertir inventario</strong> (devolver productos al stock)
                      </span>
                    </label>
                    <p style={{ fontSize: "13px", color: "#718096", marginTop: "8px", marginBottom: 0, marginLeft: "26px" }}>
                      Si activas esta opción, las cantidades de productos vendidos se
                      devolverán al inventario automáticamente.
                    </p>
                  </div>
                </div>
              </form>
            </div>

            {/* FOOTER */}
            <div
              className="card-body"
              style={{
                borderTop: "2px solid #e2e8f0",
                display: "flex",
                gap: "12px",
                paddingTop: "var(--spacing-md)",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
                style={{ flex: 1 }}
              >
                Cancelar
              </button>

              <button
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedSale || !motivo.trim()}
                style={{ flex: 1 }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Procesando...
                  </>
                ) : (
                  "✓ Crear Nota de Crédito"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
    </>
  );
}